"use client";

import React from "react";
import { FolderGit2, RotateCcw } from "lucide-react";

import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  EcommerceTermsProvider,
  useEcommerceTerms,
} from "@/integrations/wordpress/WordPressEcommerceTermsProvider";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import { WP_Term_Query } from "@/integrations/wordpress/wp_term_query";

declare const wvcClient: {
  getCurrentWPTermQueryParams: () => Record<string, unknown>;
};

function BreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="h-5 w-48 bg-muted animate-pulse" aria-hidden="true" />
    );
  }

  if (error || !links || links.length === 0) {
    return (
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/" className="hover:text-foreground">
                Home
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/ShopPage" className="hover:text-foreground">
                Shop
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Equipment catalog</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
    );
  }

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {links.map((link, index) => (
          <React.Fragment key={link.href || index}>
            <BreadcrumbItem data-index={index}>
              {index === lastIndex ? (
                <BreadcrumbPage className="font-medium text-foreground">
                  {link.title}
                </BreadcrumbPage>
              ) : (
                <BreadcrumbLink asChild>
                  <Link to={link.href} className="hover:text-foreground">
                    {link.title}
                  </Link>
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
            {index < lastIndex && <BreadcrumbSeparator />}
          </React.Fragment>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function TermsConsumer() {
  const { terms, loading, error, refetch } = useEcommerceTerms();

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="space-y-3">
          <div className="h-10 w-72 bg-muted animate-pulse" />
          <div className="h-5 max-w-xl bg-muted animate-pulse" />
        </div>
        <div className="flex flex-wrap gap-2 pt-2">
          {[0, 1, 2, 3].map((_, i) => (
            <div
              key={i}
              data-index={i}
              className="h-8 w-28 bg-muted animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4 border border-border p-6 bg-card text-card-foreground">
        <div>
          <h1 className="font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Commercial equipment catalog
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Unable to load category details at this moment.
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 border-border text-foreground hover:bg-muted"
        >
          <RotateCcw className="size-4" />
          <span>Reload categories</span>
        </Button>
      </div>
    );
  }

  const primaryTerm = terms && terms.length > 0 ? terms[0] : null;
  const pageTitle = primaryTerm?.name || "Commercial equipment catalog";
  const pageDescription =
    primaryTerm?.description ||
    "Inspect factory-tested bull machines, refurbished commercial units, and verified replacement parts.";
  const totalCount = primaryTerm?.count;

  return (
    <div className="space-y-6">
      <div className="border-b border-border pb-6">
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono font-medium tracking-wide uppercase bg-secondary text-secondary-foreground">
            <FolderGit2 className="size-3.5" />
            Equipment archive
          </span>
          {typeof totalCount === "number" && (
            <span className="text-xs font-mono text-muted-foreground">
              {totalCount} {totalCount === 1 ? "unit listed" : "units listed"}
            </span>
          )}
        </div>

        <h1 className="mt-4 font-serif text-3xl font-bold uppercase tracking-tight text-foreground sm:text-4xl md:text-5xl lg:text-6xl">
          {pageTitle}
        </h1>

        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
          {pageDescription}
        </p>
      </div>

      {terms.length > 1 && (
        <div>
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">
            Browse related categories
          </p>
          <div className="flex flex-wrap gap-2">
            {terms.map((term, i) => {
              const href = term.link || "#";
              return (
                <div key={term.id || i} data-index={i}>
                  <Button
                    asChild
                    variant="outline"
                    size="sm"
                    className="border-border bg-card text-card-foreground hover:bg-accent hover:text-accent-foreground text-xs font-medium"
                  >
                    <Link to={href}>
                      <span>{term.name}</span>
                      {typeof term.count === "number" && (
                        <span className="ml-1 text-muted-foreground">
                          ({term.count})
                        </span>
                      )}
                    </Link>
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const currentTermQueryParams =
  typeof wvcClient !== "undefined" && wvcClient.getCurrentWPTermQueryParams
    ? wvcClient.getCurrentWPTermQueryParams()
    : { taxonomy: "product_cat" };

const archiveTermQuery = new WP_Term_Query(currentTermQueryParams);

export default function ArchiveTitle() {
  return (
    <section
      id="archive-title-section"
      data-nav="light"
      className="w-full border-b border-border bg-background text-foreground py-8 md:py-12"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl space-y-6">
        <div className="overflow-x-auto py-1">
          <ShopBreadcrumbsProvider>
            <BreadcrumbsConsumer />
          </ShopBreadcrumbsProvider>
        </div>

        <EcommerceTermsProvider wp_term_query={archiveTermQuery}>
          <TermsConsumer />
        </EcommerceTermsProvider>
      </div>
    </section>
  );
}